<template>
    <WalletsTable>
        <template #filters>
            <DropdownFilter
                :pin="pinFilters"
                @apply="applyFilters"
                @clear="clearFilters"
                @toggle-pin="togglePinFilters"
            >
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <TextField
                            v-model="filtersFields.id"
                            name="id"
                            :placeholder="$t('id')"
                            :disabled="disableFilter('uid')"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <TextField
                            v-model="filtersFields.account_id"
                            name="account_id"
                            :placeholder="$t('account_id')"
                            :disabled="disableFilter('account_id')"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.type"
                            name="type"
                            :options="mapEnumToArray(AccountType)"
                            :reduce="reduceEnumValue"
                            :placeholder="$t('type')"
                            :disabled="disableFilter('type')"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.payment_account"
                            name="payment_account"
                            :options="mapEnumToArray(PaymentAccountType)"
                            :reduce="reduceEnumValue"
                            :placeholder="$t('payment_account')"
                            :disabled="disableFilter('payment_account')"
                        />
                    </label>
                </DropdownFilterItem>
            </DropdownFilter>
        </template>
    </WalletsTable>
</template>

<script setup lang="ts">
import DropdownFilter from '@src/components/DropdownFilter.vue'
import DropdownFilterItem from '@src/components/DropdownFilterItem.vue'
import SelectField from '@src/components/SelectField.vue'
import TextField from '@src/components/TextField.vue'
import { useEnum, useTable } from '@src/composables/common'
import { AccountType } from '@src/enums'
import { PaymentAccountType } from '@src/enums/PaymentAccount'
import { TableSorting } from '@src/types'
import { getWallets, WalletData } from '@src/utils/api/wallets'
import { useUserStore } from '@src/utils/stores/user'
import moment from 'moment'
import { onMounted, ref, toRefs } from 'vue'
import { useRouter } from 'vue-router'

const props = withDefaults(
    defineProps<{
        initialFilters?: {
            id?: string | null
            payment_account?: string | null
            account_id?: string | null
            type?: string | null
            is_current?: string | null
        } | null
    }>(),
    {
        initialFilters: null,
    }
)
const emit = defineEmits(['getCount', 'getWallets'])

const router = useRouter()
const userStore = useUserStore()

const wallets = ref<WalletData[]>([])
const walletsCount = ref(0)

const { initialFilters } = toRefs(props)

const { mapEnumToArray, reduceEnumValue } = useEnum()

const disableFilter = (filter: string) => {
    if (initialFilters.value) {
        return (
            Object.keys(initialFilters.value).filter((item) => {
                return item === filter
            }).length > 0
        )
    }
    return false
}
const {
    TableWrapper: WalletsTable,
    limit,
    sorting,
    page,
    filtersFields,
    filters,
    pinFilters,
    togglePinFilters,
    applyFilters,
    clearFilters,
    setFilters,
} = useTable({
    loadData: loadWallets,
    filters: {
        id: null,
        type: null,
        is_current: null,
        payment_account: null,
        account_id: null,
    },
    disableFilters: initialFilters.value
        ? Object.keys(initialFilters.value)
        : [],
    tableProps: {
        id: 'wallets-table',
        label: 'wallets',
        help: 'help_wallets_table',
        pagination: true,
        data: wallets,
        total: walletsCount,
        sorting: ref<TableSorting[]>([
            {
                field: 'created_at',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                key: 'id',
                label: 'ID',
                copy: true,
                condition: userStore.can('see_id_columns'),
                url: (wallet: WalletData) => `/wallets/${wallet.id}`,
            },
            {
                key: 'external_id',
                label: 'External ID',
                copy: true,
            },
            {
                key: 'full_name',
                label: 'Full Name',
            },
            {
                key: 'email',
                label: 'Email',
            },
            {
                key: 'type',
                label: 'Type',
                cellRenderer: (props: { rowData: WalletData }) => {
                    return props.rowData.type === AccountType.NATURAL
                        ? 'Natural'
                        : 'Legal'
                },
            },
            {
                key: 'is_current',
                label: 'Is Current',
                cellRenderer: (props: { rowData: WalletData }) => {
                    return props.rowData.is_current ? 'Yes' : 'No'
                },
            },
            {
                key: 'payment_account',
                label: 'Payment Account',
                cellRenderer: (props: { rowData: WalletData }) => {
                    return (
                        String(
                            props.rowData.payment_account.type
                        )[0].toUpperCase() +
                        String(props.rowData.payment_account.type).slice(1)
                    )
                },
            },
            {
                key: 'created_at',
                label: 'Created At',
                sorting: true,
                cellRenderer: (props: { rowData: WalletData }) => {
                    return moment(props.rowData.created_at).format('DD.MM.yyyy')
                },
            },
        ],
        actions: [
            {
                action: (wallet: WalletData) => {
                    router.push({
                        name: 'wallet',
                        params: { id: wallet.id },
                    })
                },
                icon: 'fa-eye',
                title: 'view',
                condition: () => true,
            },
        ],
    },
})

async function loadWallets() {
    const response = await getWallets(
        filters.value,
        page.value,
        limit.value,
        sorting.value
    )
    wallets.value = response.data.wallets
    walletsCount.value = response.data.wallets_count
    emit('getCount', walletsCount.value)
    emit('getWallets', wallets.value)
}

onMounted(async () => {
    if (initialFilters.value) {
        filtersFields.value = {
            ...filtersFields.value,
            ...initialFilters.value,
        }
        setFilters()
    }
    loadWallets()
})
</script>
